/*
 * Copyright (C) 2005 - 2022 TIBCO Software Inc. All rights reserved. Confidentiality & Proprietary.
 * Licensed pursuant to commercial TIBCO End User License Agreement.
 */

/* global process, global, document, __webpack_public_path__ */
import extendRequest from 'jrs-ui/src/transport/requestExtension';
import visualizeSettingsLoader from "./loader/visualizeSettingsLoader";
import logger from "js-sdk/src/common/logging/logger";
import _ from "underscore";
import $ from "jquery";
import loggerConfig from 'js-sdk/src/common/logging/loggerConfig';

extendRequest(); // Use extended request to make CORS working

// visualizejs script suffix which helps to find
// script tag with visualize script in the document
// and also it's used to determine url to
// JRS backend if it's not passed as part of a config parameter
const VISUALIZE_SCRIPT_URL = `${process.env.VISUALIZE_SCRIPT_URL}`;

// custom config singleton updated each time user calls visualize.config();
const customConfig = {
    logEnabled: loggerConfig.enabled,
    logLevel: loggerConfig.level,
    logAppenders: loggerConfig.appenders,
    _showInputControls: true
};

const absoluteUrlTestDomain = 'http://absolute.url.test.domain';

function isAbsoluteUrl(url) {
    return (new URL(url, absoluteUrlTestDomain)).origin !== absoluteUrlTestDomain;
}

function getLocation(href) {
    return new URL(href, window.location.href);
}

const getScriptLocation = _.memoize((scriptName) => {
    const scriptsArrayLike = document.getElementsByTagName('script');
    const scripts = Array.prototype.slice.call(scriptsArrayLike, 0);
    const url = scripts.map(s => s.src).find((src) => {
        return src.indexOf(scriptName) > -1;
    });

    return url && getLocation(url);
})

function buildUrl(origin, path) {
    return path.indexOf("/") === 0
        ? origin + path
        : origin + "/" + path
}

const initializeLogger = (loggerLevel, loggerEnabled, loggerAppenders) => {
    logger.initialize({
        level: loggerLevel,
        enabled: loggerEnabled,
        appenders: loggerAppenders
    });
}

const getProperties = (properties = {}) => {
    properties = {
        ...customConfig,
        ...properties
    }

    properties = enhancePropertiesWithServerLocation(properties);
    properties = enhancePropertiesFromScriptUrlParams(properties);
    properties = enhancePropertiesWithTheme(properties);

    return properties;
}

const enhancePropertiesWithServerLocation = (properties) => {
    // trying to find /client/visualize.js script in the document
    const scriptLocation = getScriptLocation(VISUALIZE_SCRIPT_URL);

    const updateServerLocationOrigin = (serverUri) => {
        const serverLocation = getLocation(serverUri);
        const differentOrigin = scriptLocation && serverLocation.origin !== scriptLocation.origin;

        const isAbsoluteServerUrl = isAbsoluteUrl(serverUri);
        if (!isAbsoluteServerUrl && differentOrigin) {
            return buildUrl(scriptLocation.origin, properties.server)
        } else {
            return serverUri;
        }
    }

    if (scriptLocation) {
        const baseUrl = scriptLocation.searchParams.get('baseUrl');
        if (properties.server) {
            properties.server = updateServerLocationOrigin(properties.server);
        } else if (baseUrl) {
            properties.server = updateServerLocationOrigin(baseUrl);
        } else {
            const scriptLocationUrlWithoutParams = `${scriptLocation.origin}${scriptLocation.pathname}`;
            properties.server = scriptLocationUrlWithoutParams.substr(0,
                scriptLocationUrlWithoutParams.length - VISUALIZE_SCRIPT_URL.length)
        }
    } else if (!properties.server) {
        throw new Error('Can not determine url of the JasperReports Server. Please pass "server" property to visualize function');
    }

    return properties;
}

const enhancePropertiesFromScriptUrlParams = (properties) => {
    const scriptLocation = getScriptLocation(VISUALIZE_SCRIPT_URL);
    const SCRIPT_URL_PARAMS = ['userLocale', 'userTimezone', 'logEnabled', 'logLevel'];

    if (scriptLocation) {
        return SCRIPT_URL_PARAMS.reduce((acc, param) => {
            const urlValue = scriptLocation.searchParams.get(param);

            if (!acc[param] && urlValue) {
                acc[param] = urlValue;
            }

            return acc;
        }, properties)
    }

    return properties;
}

const enhancePropertiesWithTheme = (properties) => {
    const themeHref = properties.theme && properties.theme.href;

    if (themeHref && !isAbsoluteUrl(themeHref)) {
        properties.theme.href = buildUrl(properties.server, themeHref);
    }

    return properties;
}

const setUpWebpackPublicPath = (properties) => {
    const scriptLocation = getScriptLocation(VISUALIZE_SCRIPT_URL);
    let publicPath = null;

    if (properties.publicPath) {
        publicPath = properties.publicPath;
    } else if (scriptLocation) {
        const publicPathSuffix = `runtime/${properties.javascriptOptions.runtimeHash}/scripts/${process.env.VISUALIZE_DIST_FOLDER}/`;
        const scriptLocationUrlWithoutParams = `${scriptLocation.origin}${scriptLocation.pathname}`;
        const scriptBase = scriptLocationUrlWithoutParams.substr(0, scriptLocationUrlWithoutParams.length - VISUALIZE_SCRIPT_URL.length);

        publicPath = `${scriptBase}/${publicPathSuffix}`;
    }

    if (publicPath) {
        // set path to load async webpack chunks
        // see https://webpack.js.org/guides/public-path/#on-the-fly
        // eslint-disable-next-line no-undef
        __webpack_public_path__ = publicPath;
    }
}

const getConfig = (param, param2, param3, param4) => {

    const defaultCallback = () => {};

    if (typeof param === 'function') {
        return {
            properties: getProperties(),
            callback: param,
            errback: param2 || defaultCallback,
            always: param3 || defaultCallback
        };
    } else {
        return {
            properties: getProperties(param),
            callback: param2 || defaultCallback,
            errback: param3 || defaultCallback,
            always: param4 || defaultCallback
        };
    }
}

const visualize = function (param, param2, param3, param4) {
    const {properties, callback, errback, always} = getConfig(param, param2, param3, param4);
    initializeLogger(properties.logLevel, properties.logEnabled, properties.logAppenders);

    const promise = visualizeSettingsLoader(properties)
        .then(function (enhancedProperties) {
            setUpWebpackPublicPath(enhancedProperties);
            return import('./visualize.common').then(({default: initViz}) => {
                return initViz(enhancedProperties);
            })
        });

    // return jQuery promise for backward compatibility
    return convertPromiseToDeferred(promise, callback, errback, always);
};

const convertPromiseToDeferred = (promise, callback, errback, always) => {
    const deferred = $.Deferred();

    promise
        .then(deferred.resolve)
        .catch(deferred.reject);

    deferred
        .then((v) => {
            callback(v);
            return $.Deferred().resolve(v);
        })
        .fail((...args) => {
            errback(...args);
            return $.Deferred().reject(...args);
        })
        .always((...args) => {
            always(...args)
        });

    return deferred.promise();
}

visualize.version = `${process.env.VISUALIZE_VERSION}`;

visualize.config = function (config) {
    Object.assign(customConfig, config);
};

// noConflict functionality
const globalVisualize = global.visualize;

visualize.noConflict = function () {
    if (global.visualize === visualize) {
        global.visualize = globalVisualize;
    }

    return visualize;
};

export default visualize;





